import React from 'react';
import { LuCircleCheck, LuCircleDashed, LuCircleX, LuLoaderCircle, LuWorkflow } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import ProgressBar from '../ui/ProgressBar';
import { isReportProcessing } from './reportStatus.js';

const STATE = {
  completed: { label: 'Terminé', icon: LuCircleCheck, iconClass: 'text-[#16a866]', tone: 'green' },
  started: { label: 'En cours', icon: LuLoaderCircle, iconClass: 'animate-spin text-brand-600 dark:text-brand-400', tone: 'brand' },
  failed: { label: 'Échec', icon: LuCircleX, iconClass: 'text-red-500', tone: 'red' },
  pending: { label: 'En attente', icon: LuCircleDashed, iconClass: 'text-gray-300 dark:text-gray-600', tone: 'gray' },
};

const percent = (done, total) => (total > 0 ? (done / total) * 100 : 0);

const Step = ({ label, status = 'pending', done = null, total = null }) => {
  const s = STATE[status] || STATE.pending;
  const Icon = s.icon;
  const hasCounts = Number.isFinite(done) && Number.isFinite(total) && total > 0;
  const value = status === 'completed' ? 100 : hasCounts ? percent(done, total) : 0;
  return (
    <li className="flex items-start gap-3">
      <Icon className={cx('mt-0.5 h-5 w-5 flex-shrink-0', s.iconClass)} aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-gray-900 dark:text-white">{label}</span>
          <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {status === 'started' && hasCounts ? `${done.toLocaleString('fr-FR')} / ${total.toLocaleString('fr-FR')}` : s.label}
          </span>
        </div>
        <ProgressBar
          className="mt-2"
          label={label}
          tone={s.tone === 'brand' ? 'brand' : s.tone}
          value={value}
          indeterminate={status === 'started' && !hasCounts}
        />
      </div>
    </li>
  );
};

/** Progress of the report's asynchronous steps; only rendered while one of them runs. */
export const ProcessingStatus = ({ report }) => {
  if (!isReportProcessing(report)) return null;
  const { steps } = report;

  return (
    <section
      aria-live="polite"
      className="rounded-2xl border border-[#d8e6fb] bg-gradient-to-b from-[#f4f8ff] to-[#fbfcfe] p-4 dark:border-gray-900 dark:from-[#0f1a2b] dark:to-[#0d1420] sm:p-5"
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-[#2f6df0] text-white" aria-hidden>
          <LuWorkflow className="h-[18px] w-[18px]" />
        </span>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Traitement en cours</h2>
            <span className="rounded bg-[#e6f0ff] px-2 py-0.5 text-[10px] font-bold tracking-wider text-[#2f6df0] dark:bg-blue-900/40 dark:text-blue-200">AUTOMATIQUE</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">La page se met à jour toute seule, inutile de la recharger.</p>
        </div>
      </div>
      <ol className="grid gap-4 md:grid-cols-3">
        <Step label="Récupération des messages" status={steps.dump || 'pending'} />
        <Step
          label="Analyse IA"
          status={steps.analyze?.status || 'pending'}
          done={steps.analyze?.analyzed_count}
          total={steps.analyze?.total_count}
        />
        <Step
          label="Téléchargement des photos"
          status={steps.download?.status || 'pending'}
          done={steps.download?.downloaded_pictures_count}
          total={steps.download?.total_pictures_count}
        />
      </ol>
    </section>
  );
};

export default ProcessingStatus;
